import api from "./apiService";

export const fetchUserProfile = async () => {
    try {
        const response = await api.get("/profile");
        
        if (response && response.data) {
            return response.data;
        } else {
            return null;
        }
    } catch (error) {
        throw error;
    }
};

// Update user profile
export const updateUserProfile = async (userData) => {
    try {
        const response = await api.put("/profile", userData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Change user password
export const changeUserPassword = async (passwordData) => {
    try {
        const response = await api.post("/profile/change-password", passwordData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetches users from the API
 * For regular users, this will only return users from their own community
 * For global admins, this will return all users
 * 
 * @param {Object|number|string} options - Can be a community ID or options object
 * @returns {Promise<Array>} Array of user objects
 */
export const fetchUsers = async (options = null) => {
    try {
        let url = '/users';
        
        // Handle both old-style and new-style parameters
        if (options !== null) {
            // If options is a number or string, treat it as a community ID (backward compatibility)
            if (typeof options === 'number' || typeof options === 'string') {
                const communityId = options;
                url += `?community_id=${communityId}`;
            } 
            // If options is an object
            else if (typeof options === 'object') {
                // If options has communityId property
                if (options.communityId) {
                    const communityId = parseInt(options.communityId) || options.communityId;
                    url += `?community_id=${communityId}`;
                }
                // If options has community_id property (alternative format)
                else if (options.community_id) {
                    const communityId = parseInt(options.community_id) || options.community_id;
                    url += `?community_id=${communityId}`;
                }
            }
        }
        
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetches a specific user by ID
 * Regular users can only fetch users from their own community
 * 
 * @param {number} userId - The ID of the user to fetch
 * @returns {Promise<Object>} User object
 */
export const fetchUser = async (userId) => {
    try {
        const response = await api.get(`/users/${userId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Send email verification
export const sendEmailVerification = async () => {
    try {
        const response = await api.post("/profile/send-verification-email");
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Verify email with token
export const verifyEmail = async (token) => {
    try {
        const response = await api.get(`/verify-email/${token}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Upload profile image
export const uploadProfileImage = async (formData) => {
    try {
        const response = await api.post("/profile/upload-image", formData, {
            headers: { 
                "Content-Type": "multipart/form-data"
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get community change requests (admin function)
export const getCommunityChangeRequests = async () => {
    try {
        const response = await api.get("/admin/community-requests");
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Get user verification status
export const getUserVerificationStatus = async (userId) => {
    try {
        const response = await api.get(`/admin/users/${userId}/verification`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Update user language preference
export const updateLanguagePreference = async (language) => {
    try {
        const response = await api.put("/profile/language", { language });
        return response.data;
    } catch (error) {
        throw error;
    }
};